import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  fetchPersonalTextSource,
  fetchPhoneContactSync,
  setPersonalTextSync,
  setPhoneContactSync,
} from "../../lib/queries";
import { useSession } from "../../session";

/**
 * The switch on the sync from your own phone.
 *
 * Only ever reached by the person whose phone it is — the section is not in the
 * menu for anybody else, and the database refuses the change regardless of what
 * a screen offers. Since 2026-09-20 it is your switch rather than the switch:
 * more than one person syncs, and one of them turning theirs off must not
 * silently stop somebody else's phone (ADR 0178).
 *
 * The help text has to say two things a switch does not say by itself: how long
 * it takes, and what happens to the texts that arrive while it is off. Both are
 * things somebody will otherwise find out by being surprised.
 */
export function PersonalTextsTool() {
  const { profile } = useSession();
  const sync = useAsync(fetchPersonalTextSource, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (profile?.syncs_personal_texts !== true) {
    return (
      <div className="stub">
        {t("Your phone is not set up to sync into Roof Board.")}
      </div>
    );
  }

  if (sync.loading) return <div className="stub">{t("Checking…")}</div>;

  const on = sync.data?.sync_enabled === true;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await setPersonalTextSync(!on);
      await sync.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ErrorNote error={sync.error ?? error} />

      <div className="rrow">
        <span className="k">{t("Sync my personal texts")}</span>
        <span className="v">
          <span className={`badge ${on ? "approved" : "draft"}`}>
            {on ? t("on") : t("off")}
          </span>
        </span>
      </div>

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
        {on
          ? t(
              "Texts on your own phone are being copied onto the customer's profile — but only where the number already belongs to a contact in Roof Board. Everything else your phone sees is dropped and nothing about it is stored.",
            )
          : t("Nothing from your phone is being stored.")}
      </p>

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
        {t(
          "Turning this off takes about a minute to take effect. While it is off, texts are dropped rather than held back — turning it on again picks up from that moment, not from where it left. The one-time backfill script can fill an off-period in later if you ever want it.",
        )}
      </p>

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
        {t(
          "A synced text is visible to anyone who can open that customer’s profile, and it says whose phone it came from. If one should not be there, delete it on the Texts card — you are the only person who can delete one of yours.",
        )}
      </p>

      <button
        className="addrow"
        disabled={busy}
        style={{ marginTop: 12 }}
        onClick={() => void toggle()}
      >
        {busy
          ? t("Saving…")
          : on
            ? t("Turn syncing off")
            : t("Turn syncing on")}
      </button>

      <MacKey value={sync.data?.owner_key ?? null} />

      <hr
        style={{
          margin: "24px 0",
          border: 0,
          borderTop: "1px solid var(--line)",
        }}
      />

      <PhoneContactsSwitch />
    </>
  );
}

/**
 * The key that tells Roof Board which phone a text came from.
 *
 * Shown here because this is the one screen only its owner can reach, and
 * because the alternative — somebody else reading it out to them — is a
 * credential travelling through a text message. It goes into the watcher script
 * on their Mac once, beside the ingest secret.
 *
 * Hidden until asked for. Not for secrecy, which the policy on the table
 * handles: a string of 64 characters sitting under a switch looks like
 * something that has gone wrong, and the people using this screen are the
 * people it would worry.
 */
function MacKey({ value }: { value: string | null }) {
  const [shown, setShown] = useState(false);

  if (!value) return null;

  return (
    <p className="na-hint" style={{ marginTop: 16, maxWidth: 560 }}>
      {t("Your Mac identifies itself with a key, set once in the sync script.")}{" "}
      {shown ? (
        <code
          style={{ wordBreak: "break-all", userSelect: "all" }}
          data-testid="mac-key"
        >
          {value}
        </code>
      ) : (
        <button
          type="button"
          className="linkbtn"
          onClick={() => setShown(true)}
        >
          {t("Show my key")}
        </button>
      )}
    </p>
  );
}

/**
 * The other direction: leads out of Roof Board and into the phone's address
 * book.
 *
 * Same Mac, same person, so it lives under the same heading rather than
 * inventing a second section somebody has to know about. What it must say
 * plainly is the thing a switch cannot: turning it off does not empty the
 * phone, because nothing here can reach a phone.
 */
function PhoneContactsSwitch() {
  const sync = useAsync(fetchPhoneContactSync, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (sync.loading) return <div className="stub">{t("Checking…")}</div>;

  const on = sync.data === true;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await setPhoneContactSync(!on);
      await sync.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ErrorNote error={sync.error ?? error} />

      <div className="rrow">
        <span className="k">{t("Contacts on my phone")}</span>
        <span className="v">
          <span className={`badge ${on ? "approved" : "draft"}`}>
            {on ? t("on") : t("off")}
          </span>
        </span>
      </div>

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
        {on
          ? t(
              "Every lead is being filed in your phone's address book, in a group called Roof Board, with their address, a link to their CompanyCam album and a link back here. Your Mac has to be on for it to happen.",
            )
          : t("Nothing is being sent to your address book.")}
      </p>

      <p className="na-hint" style={{ marginTop: 10, maxWidth: 560 }}>
        {t(
          "Turning this off stops new leads going across, within about ten minutes. It does not empty your phone: the contacts already there stay there. Remove them by deleting the Roof Board group’s members in Contacts on your Mac.",
        )}
      </p>

      <button
        className="addrow"
        disabled={busy}
        style={{ marginTop: 12 }}
        onClick={() => void toggle()}
      >
        {busy
          ? t("Saving…")
          : on
            ? t("Stop filing leads")
            : t("File leads in my phone")}
      </button>
    </>
  );
}
